import React, { useState, useEffect } from 'react';
import {
    Container, Box, Typography, Button, Paper, Alert, Snackbar,
    Grid, Card, CardContent, CardHeader, List, ListItem, ListItemText,
    Chip, Avatar, Stack, Divider, IconButton,
    Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
    Dialog, DialogTitle, DialogContent, DialogActions, Badge,
    FormControl, InputLabel, Select, MenuItem, Tabs, Tab, Tooltip, CircularProgress
} from '@mui/material';
import {
    Dashboard as DashboardIcon,
    Flight as FlightIcon,
    Warning as WarningIcon,
    CheckCircle as CheckCircleIcon,
    Schedule as ScheduleIcon,
    Person as PersonIcon,
    Email as EmailIcon,
    Refresh as RefreshIcon,
    CalendarMonth as CalendarIcon,
    TrendingUp as TrendingUpIcon,
    LocationOn as LocationIcon,
    Business as BusinessIcon,
    Notifications as NotificationsIcon
} from '@mui/icons-material';
import TravelCalendar from '../components/TravelCalendar';
import TravelAnalytics from '../components/TravelAnalytics';
import { getDabUrl } from '../services/api';
import { sendApprovalEmail, sendReminderEmail } from '../services/email';
import { getClientPrincipal } from '../services/auth';

interface TravelFormDashboard {
    id: number;
    staffName: string;
    email: string;
    projectNumber: string;
    projectName: string;
    client: string;
    teamLeader?: string;
    dutyOfficer?: string;
    destination: string[];
    travelDateFrom: string;
    travelDateTo: string;
    status: string;
    submittedDate: string | null;
    lastModified: string;
    department?: string;
    travelType: 'project' | 'travel';
}

const mockAllTravelForms: TravelFormDashboard[] = [
    {
        id: 1,
        staffName: 'John Smith',
        email: 'john.smith@iodparc.com',
        projectNumber: 'P2024-001',
        projectName: 'Digital Transformation Initiative',
        client: 'TechCorp Ltd',
        destination: ['United States', 'Canada'],
        travelDateFrom: '2024-02-15',
        travelDateTo: '2024-02-22',
        status: 'approved',
        submittedDate: '2024-01-20',
        lastModified: '2024-01-18',
        department: 'Technology',
        travelType: 'project'
    },
    {
        id: 2,
        staffName: 'Sarah Johnson',
        email: 'sarah.johnson@iodparc.com',
        projectNumber: 'P2024-002',
        projectName: 'Infrastructure Modernization',
        client: 'GlobalBank PLC',
        destination: ['United Kingdom'],
        travelDateFrom: '2024-03-10',
        travelDateTo: '2024-03-15',
        status: 'submitted',
        submittedDate: '2024-02-01',
        lastModified: '2024-02-01',
        department: 'Consulting',
        travelType: 'project'
    },
    {
        id: 3,
        staffName: 'Michael Brown',
        email: 'michael.brown@iodparc.com',
        projectNumber: 'P2023-045',
        projectName: 'Supply Chain Optimization',
        client: 'Manufacturing Co',
        destination: ['Germany', 'France'],
        travelDateFrom: '2024-04-05',
        travelDateTo: '2024-04-12',
        status: 'draft',
        submittedDate: null,
        lastModified: '2024-01-25',
        department: 'Operations',
        travelType: 'travel'
    },
    {
        id: 4,
        staffName: 'Emily Davis',
        email: 'emily.davis@iodparc.com',
        projectNumber: 'P2024-003',
        projectName: 'Customer Experience Platform',
        client: 'RetailChain Inc',
        destination: ['Japan'],
        travelDateFrom: '2024-02-28',
        travelDateTo: '2024-03-05',
        status: 'draft',
        submittedDate: null,
        lastModified: '2024-02-03',
        department: 'Design',
        travelType: 'project'
    },
    {
        id: 5,
        staffName: 'David Wilson',
        email: 'david.wilson@iodparc.com',
        projectNumber: 'P2024-004',
        projectName: 'Digital Marketing Strategy',
        client: 'StartupTech Inc',
        destination: ['Singapore'],
        travelDateFrom: '2024-03-01',
        travelDateTo: '2024-03-03',
        status: 'submitted',
        submittedDate: '2024-02-05',
        lastModified: '2024-02-04',
        department: 'Marketing',
        travelType: 'travel'
    }
];

interface TabPanelProps {
    children?: React.ReactNode;
    index: number;
    value: number;
}

const TabPanel = (props: TabPanelProps) => {
    const { children, value, index, ...other } = props;
    return (
        <div
            role="tabpanel"
            hidden={value !== index}
            id={`dashboard-tabpanel-${index}`}
            aria-labelledby={`dashboard-tab-${index}`}
            {...other}
        >
            {value === index && <Box sx={{ pt: 3 }}>{children}</Box>}
        </div>
    );
};

const getStatusColor = (status: string) => {
    switch (status) {
        case 'approved': return 'success';
        case 'submitted': return 'info';
        case 'draft': return 'warning';
        default: return 'default';
    }
};

const getInitials = (name: string) => {
    return name.split(' ').map(n => n[0]).join('').toUpperCase();
};

const TravelDashboardPage: React.FC = () => {
    const [travelForms, setTravelForms] = useState<TravelFormDashboard[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [selectedTab, setSelectedTab] = useState(0);
    const [historicalForms, setHistoricalForms] = useState<TravelFormDashboard[]>([]);
    const [snackbarOpen, setSnackbarOpen] = useState(false);
    const [snackbarMessage, setSnackbarMessage] = useState('');
    const [userEmail, setUserEmail] = useState<string | null>(null);
    const [accessDenied, setAccessDenied] = useState(false);

    // Authorized users for Travel Dashboard
    const TRAVEL_ADMIN_EMAILS = [
        'si@iodparc.com',
        'diane@iodparc.com',
        'pam@iodparc.com',
        'sadie@iodparc.com',
        'hatty@iodparc.com'
    ];

    // Check access on mount
    useEffect(() => {
        checkAccess();
    }, []);

    const checkAccess = async () => {
        try {
            const principal = await getClientPrincipal();
            const email = principal?.userDetails?.toLowerCase() || null;
            setUserEmail(email);

            if (!email || !TRAVEL_ADMIN_EMAILS.includes(email)) {
                setAccessDenied(true);
                setLoading(false);
                return;
            }

            loadTravelForms();
        } catch (err) {
            console.error('Error checking access:', err);
            setAccessDenied(true);
            setLoading(false);
        }
    };

    const loadTravelForms = async () => {
        try {
            setLoading(true);
            const res = await fetch(getDabUrl('TravelOverview'), {
                credentials: 'include'
            });
            if (!res.ok) throw new Error('Failed to fetch travel data');

            const data = await res.json();
            const items = data?.value || data || [];

            // Map API data to dashboard format
            const mapped: TravelFormDashboard[] = items.map((item: any) => ({
                id: item.id,
                staffName: item.owner_name || 'Unknown',
                email: item.owner_email || item.owner_user_id || '',
                projectNumber: item.project_number,
                projectName: item.project_name || '',
                client: item.client || '',
                destination: [], // Countries would need to be fetched separately
                travelDateFrom: item.travel_date_from,
                travelDateTo: item.travel_date_to,
                status: item.status,
                submittedDate: item.created_at || null,
                lastModified: item.last_modified || item.created_at || '',
                department: '', // Not in database
                travelType: item.travel_type || 'project'
            }));

            setTravelForms(mapped);

            // Separate historical forms (approved/rejected)
            const historical = mapped.filter(f => f.status === 'approved' || f.status === 'rejected');
            setHistoricalForms(historical);

            setError(null);
        } catch (e) {
            console.error('Failed to load travel forms:', e);
            setError('Failed to load travel data');
            // Fallback to mock data for now
            setTravelForms(mockAllTravelForms);
        } finally {
            setLoading(false);
        }
    };
    const [selectedForm, setSelectedForm] = useState<any>(null);
    const [showApprovalDialog, setShowApprovalDialog] = useState(false);
    const [showViewDialog, setShowViewDialog] = useState(false);
    const [fullFormData, setFullFormData] = useState<any>(null);
    const [loadingFormDetails, setLoadingFormDetails] = useState(false);

    // Dashboard calculations
    const currentTravellers = travelForms.filter(form => {
        const today = new Date();
        const travelFrom = new Date(form.travelDateFrom);
        const travelTo = new Date(form.travelDateTo);
        return today >= travelFrom && today <= travelTo && form.status === 'approved';
    });

    const upcomingTravel = travelForms.filter(form => {
        const today = new Date();
        const travelFrom = new Date(form.travelDateFrom);
        const next60Days = new Date(today.getTime() + (60 * 24 * 60 * 60 * 1000));
        return travelFrom >= today && travelFrom <= next60Days && form.status === 'approved';
    });

    const pendingForms = travelForms.filter(form => form.status === 'submitted');
    const incompleteForms = travelForms.filter(form => form.status === 'draft');

    const travelConflicts = travelForms.filter((form, index) => {
        return travelForms.some((otherForm, otherIndex) => {
            // Skip same form, or if they're DIFFERENT people (conflicts only matter for same person)
            if (index >= otherIndex || form.staffName !== otherForm.staffName) return false;

            const form1Start = new Date(form.travelDateFrom);
            const form1End = new Date(form.travelDateTo);
            const form2Start = new Date(otherForm.travelDateFrom);
            const form2End = new Date(otherForm.travelDateTo);

            return (form1Start <= form2End && form1End >= form2Start) &&
                (form.status === 'approved' || form.status === 'submitted') &&
                (otherForm.status === 'approved' || otherForm.status === 'submitted');
        });
    });

    const handleApproveForm = (formId: number) => {
        const form = travelForms.find(f => f.id === formId);
        if (form) {
            setSelectedForm(form);
            setShowApprovalDialog(true);
        }
    };

    const handleViewForm = async (formId: number) => {
        const form = travelForms.find(f => f.id === formId);
        if (form) {
            setSelectedForm(form);
            setShowViewDialog(true);
            setLoadingFormDetails(true);

            try {
                // Fetch full form details including accommodations and segments
                const res = await fetch(getDabUrl('GetTravel'), {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({ form_id: formId }),
                    credentials: 'include'
                });

                if (res.ok) {
                    const data = await res.json();
                    const fullData = data?.value?.[0] || data;

                    // Fetch accommodations
                    const accomRes = await fetch(getDabUrl(`TravelAccommodations?$filter=travel_form_id eq ${formId}`), {
                        credentials: 'include'
                    });
                    const accomData = await accomRes.json();
                    fullData.accommodations = accomData?.value || [];

                    // Fetch travel segments
                    const segRes = await fetch(getDabUrl(`TravelSegments?$filter=travel_form_id eq ${formId}`), {
                        credentials: 'include'
                    });
                    const segData = await segRes.json();
                    fullData.segments = segData?.value || [];

                    setFullFormData(fullData);
                }
            } catch (error) {
                console.error('Failed to load form details:', error);
            } finally {
                setLoadingFormDetails(false);
            }
        }
    };

    const confirmApproval = async () => {
        if (!selectedForm) return;

        try {
            // Call stored procedure to approve the form
            // Note: ApproveTravel may not exist - using UpdateTravel instead
            const res = await fetch(getDabUrl(`TravelForms/id/${selectedForm.id}`), {
                method: 'PATCH',
                headers: { 'Content-Type': 'application/json' },
                credentials: 'include',
                body: JSON.stringify({
                    status: 'approved',
                    approved_at: new Date().toISOString(),
                    approved_by: 'travel@iodparc.com'
                })
            });

            if (!res.ok) {
                const errorData = await res.json().catch(() => null);
                console.error('Approval failed:', errorData);
                throw new Error('Approval failed');
            }

            // Update local state
            setTravelForms(prev =>
                prev.map(form =>
                    form.id === selectedForm.id
                        ? { ...form, status: 'approved' }
                        : form
                )
            );

            // Send approval email to user
            const emailSent = await sendApprovalEmail({
                owner_email: selectedForm.email,
                owner_name: selectedForm.staffName,
                project_number: selectedForm.projectNumber,
                project_name: selectedForm.projectName,
                client: selectedForm.client,
                travel_date_from: selectedForm.travelDateFrom,
                travel_date_to: selectedForm.travelDateTo,
                approved_by: 'travel@iodparc.com',
                travel_type: selectedForm.travelType
            });

            const message = emailSent
                ? `Travel form approved and email sent to ${selectedForm.staffName}`
                : `Travel form approved (email not configured)`;
            setSnackbarMessage(message);
            setSnackbarOpen(true);
            setShowApprovalDialog(false);
            setSelectedForm(null);

            // Reload data to get updated counts
            await loadTravelForms();
        } catch (error) {
            console.error('Approval error:', error);
            setSnackbarMessage('Failed to approve form. Please try again.');
            setSnackbarOpen(true);
        }
    };

    const handleSendReminder = async (formId: number) => {
        const form = travelForms.find(f => f.id === formId);
        if (!form) return;

        // Validate email address
        if (!form.email || !form.email.includes('@')) {
            setSnackbarMessage(`Cannot send reminder: No valid email address for ${form.staffName}`);
            setSnackbarOpen(true);
            return;
        }

        try {
            const emailSent = await sendReminderEmail({
                owner_email: form.email,
                owner_name: form.staffName,
                project_number: form.projectNumber,
                project_name: form.projectName,
                travel_date_from: form.travelDateFrom,
                travel_date_to: form.travelDateTo,
                travel_type: form.travelType
            });

            const message = emailSent
                ? `Reminder sent to ${form.staffName} (${form.email})`
                : `Failed to send reminder (email not configured)`;

            setSnackbarMessage(message);
            setSnackbarOpen(true);
        } catch (error) {
            console.error('Failed to send reminder:', error);
            setSnackbarMessage('Failed to send reminder');
            setSnackbarOpen(true);
        }
    };

    const formatDate = (dateString: string) => {
        return new Date(dateString).toLocaleDateString('en-GB');
    };

    const getDaysUntilTravel = (dateString: string) => {
        const today = new Date();
        const travelDate = new Date(dateString);
        const diffTime = travelDate.getTime() - today.getTime();
        const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
        return diffDays;
    };

    if (accessDenied) {
        return (
            <Container maxWidth="lg" sx={{ py: 4 }}>
                <Paper elevation={3} sx={{ p: 3 }}>
                    <Alert severity="error" sx={{ mb: 2 }}>
                        <Typography variant="h6" gutterBottom>
                            Access Denied
                        </Typography>
                        <Typography>
                            You do not have permission to access the Travel Dashboard.
                            This page is restricted to authorized travel operations personnel only.
                        </Typography>
                        {userEmail && (
                            <Typography variant="body2" sx={{ mt: 1 }}>
                                Current user: {userEmail}
                            </Typography>
                        )}
                    </Alert>
                </Paper>
            </Container>
        );
    }

    if (loading) {
        return (
            <Container maxWidth="xl">
                <Box sx={{ py: 8, textAlign: 'center' }}>
                    <CircularProgress />
                    <Typography sx={{ mt: 2 }}>Loading travel dashboard...</Typography>
                </Box>
            </Container>
        );
    }

    return (
        <Container maxWidth="xl">
            <Box sx={{ py: 3 }}>
                {/* Error Alert */}
                {error && (
                    <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>
                        {error}
                    </Alert>
                )}

                {/* Header */}
                <Paper sx={{ p: 3, mb: 3 }}>
                    <Stack direction="row" alignItems="center" spacing={2}>
                        <DashboardIcon color="primary" sx={{ fontSize: 32 }} />
                        <Box sx={{ flexGrow: 1 }}>
                            <Typography variant="h4" component="h1" gutterBottom>
                                Travel Operations Dashboard
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                                Monitor and manage all staff travel requests and compliance
                            </Typography>
                        </Box>
                        <Button
                            variant="outlined"
                            startIcon={<RefreshIcon />}
                            onClick={() => {
                                setSnackbarMessage('Dashboard refreshed');
                                setSnackbarOpen(true);
                            }}
                        >
                            Refresh
                        </Button>
                    </Stack>
                </Paper>

                {/* Overview Stats */}
                <Grid container spacing={3} sx={{ mb: 3 }}>
                    <Grid item xs={12} sm={6} md={3}>
                        <Card>
                            <CardContent sx={{ textAlign: 'center' }}>
                                <Stack direction="row" alignItems="center" justifyContent="center" spacing={1} sx={{ mb: 1 }}>
                                    <FlightIcon color="primary" />
                                    <Typography variant="h4" color="primary">
                                        {currentTravellers.length}
                                    </Typography>
                                </Stack>
                                <Typography variant="body2" color="text.secondary">
                                    Currently Travelling
                                </Typography>
                            </CardContent>
                        </Card>
                    </Grid>
                    <Grid item xs={12} sm={6} md={3}>
                        <Card>
                            <CardContent sx={{ textAlign: 'center' }}>
                                <Stack direction="row" alignItems="center" justifyContent="center" spacing={1} sx={{ mb: 1 }}>
                                    <CalendarIcon color="success" />
                                    <Typography variant="h4" color="success.main">
                                        {upcomingTravel.length}
                                    </Typography>
                                </Stack>
                                <Typography variant="body2" color="text.secondary">
                                    Upcoming (Next 7 Days)
                                </Typography>
                            </CardContent>
                        </Card>
                    </Grid>
                    <Grid item xs={12} sm={6} md={3}>
                        <Card>
                            <CardContent sx={{ textAlign: 'center' }}>
                                <Stack direction="row" alignItems="center" justifyContent="center" spacing={1} sx={{ mb: 1 }}>
                                    <Badge badgeContent={pendingForms.length} color="info">
                                        <ScheduleIcon color="info" />
                                    </Badge>
                                    <Typography variant="h4" color="info.main">
                                        {pendingForms.length}
                                    </Typography>
                                </Stack>
                                <Typography variant="body2" color="text.secondary">
                                    Pending Approval
                                </Typography>
                            </CardContent>
                        </Card>
                    </Grid>
                    <Grid item xs={12} sm={6} md={3}>
                        <Card>
                            <CardContent sx={{ textAlign: 'center' }}>
                                <Stack direction="row" alignItems="center" justifyContent="center" spacing={1} sx={{ mb: 1 }}>
                                    <Badge badgeContent={incompleteForms.length} color="warning">
                                        <WarningIcon color="warning" />
                                    </Badge>
                                    <Typography variant="h4" color="warning.main">
                                        {incompleteForms.length}
                                    </Typography>
                                </Stack>
                                <Typography variant="body2" color="text.secondary">
                                    Incomplete Forms
                                </Typography>
                            </CardContent>
                        </Card>
                    </Grid>
                </Grid>

                {/* Tabbed Content */}
                <Paper>
                    <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
                        <Tabs
                            value={selectedTab}
                            onChange={(_, newValue) => setSelectedTab(newValue)}
                            variant="scrollable"
                            scrollButtons="auto"
                        >
                            <Tab label="Overview" icon={<DashboardIcon />} iconPosition="start" />
                            <Tab
                                label={`Pending Approval (${pendingForms.length})`}
                                icon={<ScheduleIcon />}
                                iconPosition="start"
                            />
                            <Tab
                                label={`Incomplete (${incompleteForms.length})`}
                                icon={<WarningIcon />}
                                iconPosition="start"
                            />
                            <Tab label={`Historical Travel (${historicalForms.length})`} icon={<CheckCircleIcon />} iconPosition="start" />
                            <Tab label="Staff Analytics" icon={<TrendingUpIcon />} iconPosition="start" />
                            <Tab label="Travel Calendar" icon={<CalendarIcon />} iconPosition="start" />
                        </Tabs>
                    </Box>

                    {/* Overview Tab */}
                    <TabPanel value={selectedTab} index={0}>
                        <Grid container spacing={3}>
                            {/* Current Travellers */}
                            <Grid item xs={12} md={6}>
                                <Card>
                                    <CardHeader
                                        title="Currently Travelling"
                                        avatar={<FlightIcon color="primary" />}
                                    />
                                    <CardContent>
                                        {currentTravellers.length === 0 ? (
                                            <Typography variant="body2" color="text.secondary">
                                                No staff currently travelling
                                            </Typography>
                                        ) : (
                                            <List dense>
                                                {currentTravellers.map(form => (
                                                    <ListItem key={form.id}>
                                                        <Avatar sx={{ mr: 2, bgcolor: 'primary.main' }}>
                                                            {getInitials(form.staffName)}
                                                        </Avatar>
                                                        <ListItemText
                                                            primary={form.staffName}
                                                            secondary={`${form.destination.join(', ')} • ${form.client}`}
                                                        />
                                                    </ListItem>
                                                ))}
                                            </List>
                                        )}
                                    </CardContent>
                                </Card>
                            </Grid>

                            {/* Upcoming Travel */}
                            <Grid item xs={12} md={6}>
                                <Card>
                                    <CardHeader
                                        title="Upcoming Travel (Next 7 Days)"
                                        avatar={<CalendarIcon color="success" />}
                                    />
                                    <CardContent>
                                        {upcomingTravel.length === 0 ? (
                                            <Typography variant="body2" color="text.secondary">
                                                No upcoming travel in the next 7 days
                                            </Typography>
                                        ) : (
                                            <List dense>
                                                {upcomingTravel.map(form => (
                                                    <ListItem key={form.id}>
                                                        <Avatar sx={{ mr: 2, bgcolor: 'success.main' }}>
                                                            {getInitials(form.staffName)}
                                                        </Avatar>
                                                        <ListItemText
                                                            primary={form.staffName}
                                                            secondary={`${form.destination.join(', ')} • Departs ${formatDate(form.travelDateFrom)}`}
                                                        />
                                                        <Typography variant="caption" color="text.secondary">
                                                            {getDaysUntilTravel(form.travelDateFrom)}d
                                                        </Typography>
                                                    </ListItem>
                                                ))}
                                            </List>
                                        )}
                                    </CardContent>
                                </Card>
                            </Grid>

                            {/* Travel Conflicts */}
                            {travelConflicts.length > 0 && (
                                <Grid item xs={12}>
                                    <Card>
                                        <CardHeader
                                            title="Travel Conflicts Detected"
                                            avatar={<WarningIcon color="error" />}
                                        />
                                        <CardContent>
                                            <Alert severity="warning" sx={{ mb: 2 }}>
                                                The following travel dates may conflict with other approved travel
                                            </Alert>
                                            <List>
                                                {travelConflicts.map(form => (
                                                    <ListItem key={form.id}>
                                                        <ListItemText
                                                            primary={`${form.staffName} - ${form.destination.join(', ')}`}
                                                            secondary={`${formatDate(form.travelDateFrom)} - ${formatDate(form.travelDateTo)} • ${form.projectName}`}
                                                        />
                                                        <Chip
                                                            label={form.status.toUpperCase()}
                                                            color={getStatusColor(form.status) as any}
                                                            size="small"
                                                        />
                                                    </ListItem>
                                                ))}
                                            </List>
                                        </CardContent>
                                    </Card>
                                </Grid>
                            )}
                        </Grid>
                    </TabPanel>

                    {/* Pending Approval Tab */}
                    <TabPanel value={selectedTab} index={1}>
                        <TableContainer>
                            <Table>
                                <TableHead>
                                    <TableRow>
                                        <TableCell><strong>Staff Member</strong></TableCell>
                                        <TableCell><strong>Type</strong></TableCell>
                                        <TableCell><strong>Project</strong></TableCell>
                                        <TableCell><strong>Destination</strong></TableCell>
                                        <TableCell><strong>Travel Dates</strong></TableCell>
                                        <TableCell><strong>Submitted</strong></TableCell>
                                        <TableCell align="center"><strong>Actions</strong></TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {pendingForms.map(form => (
                                        <TableRow key={form.id} hover>
                                            <TableCell>
                                                <Stack direction="row" alignItems="center" spacing={2}>
                                                    <Avatar sx={{ bgcolor: 'info.main' }}>
                                                        {getInitials(form.staffName)}
                                                    </Avatar>
                                                    <Box>
                                                        <Typography variant="body2" fontWeight="medium">
                                                            {form.staffName}
                                                        </Typography>
                                                        <Typography variant="caption" color="text.secondary">
                                                            {form.department}
                                                        </Typography>
                                                    </Box>
                                                </Stack>
                                            </TableCell>
                                            <TableCell>
                                                <Chip
                                                    label={form.travelType === 'project' ? 'Project' : 'Travel'}
                                                    size="small"
                                                    color={form.travelType === 'project' ? 'primary' : 'default'}
                                                    variant="outlined"
                                                />
                                            </TableCell>
                                            <TableCell>
                                                {form.travelType === 'project' ? (
                                                    <Box>
                                                        <Typography variant="body2" fontWeight="medium">
                                                            {form.projectName}
                                                        </Typography>
                                                        <Typography variant="caption" color="text.secondary">
                                                            {form.projectNumber} • {form.client}
                                                        </Typography>
                                                    </Box>
                                                ) : (
                                                    <Typography variant="body2" color="text.secondary">
                                                        —
                                                    </Typography>
                                                )}
                                            </TableCell>
                                            <TableCell>
                                                {form.destination.join(', ')}
                                            </TableCell>
                                            <TableCell>
                                                <Typography variant="body2">
                                                    {formatDate(form.travelDateFrom)} - {formatDate(form.travelDateTo)}
                                                </Typography>
                                            </TableCell>
                                            <TableCell>
                                                <Typography variant="body2">
                                                    {form.submittedDate ? formatDate(form.submittedDate) : 'Not submitted'}
                                                </Typography>
                                            </TableCell>
                                            <TableCell align="center">
                                                <Stack direction="row" spacing={1} justifyContent="center">
                                                    <Tooltip title="View Form Details">
                                                        <Button
                                                            variant="outlined"
                                                            size="small"
                                                            onClick={() => handleViewForm(form.id)}
                                                        >
                                                            View
                                                        </Button>
                                                    </Tooltip>
                                                    <Tooltip title="Approve Travel">
                                                        <Button
                                                            variant="contained"
                                                            size="small"
                                                            color="success"
                                                            onClick={() => handleApproveForm(form.id)}
                                                        >
                                                            Approve
                                                        </Button>
                                                    </Tooltip>
                                                </Stack>
                                            </TableCell>
                                        </TableRow>
                                    ))}
                                </TableBody>
                            </Table>
                        </TableContainer>
                        {pendingForms.length === 0 && (
                            <Box sx={{ textAlign: 'center', py: 4 }}>
                                <CheckCircleIcon sx={{ fontSize: 64, color: 'success.main', mb: 2 }} />
                                <Typography variant="h6" gutterBottom>
                                    No forms pending approval
                                </Typography>
                                <Typography variant="body2" color="text.secondary">
                                    All submitted travel forms have been processed
                                </Typography>
                            </Box>
                        )}
                    </TabPanel>

                    {/* Incomplete Forms Tab */}
                    <TabPanel value={selectedTab} index={2}>
                        <TableContainer>
                            <Table>
                                <TableHead>
                                    <TableRow>
                                        <TableCell><strong>Staff Member</strong></TableCell>
                                        <TableCell><strong>Type</strong></TableCell>
                                        <TableCell><strong>Project</strong></TableCell>
                                        <TableCell><strong>Last Modified</strong></TableCell>
                                        <TableCell><strong>Days Until Travel</strong></TableCell>
                                        <TableCell align="center"><strong>Actions</strong></TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {incompleteForms.map(form => (
                                        <TableRow key={form.id} hover>
                                            <TableCell>
                                                <Stack direction="row" alignItems="center" spacing={2}>
                                                    <Avatar sx={{ bgcolor: 'warning.main' }}>
                                                        {getInitials(form.staffName)}
                                                    </Avatar>
                                                    <Box>
                                                        <Typography variant="body2" fontWeight="medium">
                                                            {form.staffName}
                                                        </Typography>
                                                        <Typography variant="caption" color="text.secondary">
                                                            {form.email}
                                                        </Typography>
                                                    </Box>
                                                </Stack>
                                            </TableCell>
                                            <TableCell>
                                                <Chip
                                                    label={form.travelType === 'project' ? 'Project' : 'Travel'}
                                                    size="small"
                                                    color={form.travelType === 'project' ? 'primary' : 'default'}
                                                    variant="outlined"
                                                />
                                            </TableCell>
                                            <TableCell>
                                                {form.travelType === 'project' ? (
                                                    <Box>
                                                        <Typography variant="body2" fontWeight="medium">
                                                            {form.projectName}
                                                        </Typography>
                                                        <Typography variant="caption" color="text.secondary">
                                                            {form.destination.join(', ')}
                                                        </Typography>
                                                    </Box>
                                                ) : (
                                                    <Typography variant="body2" color="text.secondary">
                                                        {form.destination.join(', ') || '—'}
                                                    </Typography>
                                                )}
                                            </TableCell>
                                            <TableCell>
                                                <Typography variant="body2">
                                                    {formatDate(form.travelDateFrom)}
                                                </Typography>
                                            </TableCell>
                                            <TableCell>
                                                <Chip
                                                    label={`${getDaysUntilTravel(form.travelDateFrom)} days`}
                                                    color={getDaysUntilTravel(form.travelDateFrom) <= 7 ? 'error' : 'warning'}
                                                    size="small"
                                                />
                                            </TableCell>
                                            <TableCell align="center">
                                                {form.status === 'draft' ? (
                                                    <Tooltip title="Send Reminder to Complete Form">
                                                        <Button
                                                            variant="outlined"
                                                            size="small"
                                                            startIcon={<EmailIcon />}
                                                            onClick={() => handleSendReminder(form.id)}
                                                        >
                                                            Remind
                                                        </Button>
                                                    </Tooltip>
                                                ) : (
                                                    <Chip
                                                        label="Submitted"
                                                        color="info"
                                                        size="small"
                                                    />
                                                )}
                                            </TableCell>
                                        </TableRow>
                                    ))}
                                </TableBody>
                            </Table>
                        </TableContainer>
                        {incompleteForms.length === 0 && (
                            <Box sx={{ textAlign: 'center', py: 4 }}>
                                <CheckCircleIcon sx={{ fontSize: 64, color: 'success.main', mb: 2 }} />
                                <Typography variant="h6" gutterBottom>
                                    All forms are complete
                                </Typography>
                                <Typography variant="body2" color="text.secondary">
                                    No incomplete forms requiring follow-up
                                </Typography>
                            </Box>
                        )}
                    </TabPanel>

                    {/* Historical Travel Tab */}
                    <TabPanel value={selectedTab} index={3}>
                        <Typography variant="h6" gutterBottom>
                            Historical Travel Records
                        </Typography>
                        <TableContainer component={Paper}>
                            <Table>
                                <TableHead>
                                    <TableRow>
                                        <TableCell>Staff</TableCell>
                                        <TableCell>Type</TableCell>
                                        <TableCell>Project</TableCell>
                                        <TableCell>Destination</TableCell>
                                        <TableCell>Travel Dates</TableCell>
                                        <TableCell>Status</TableCell>
                                        <TableCell>Submitted</TableCell>
                                        <TableCell align="center">Actions</TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {historicalForms.map((form) => (
                                        <TableRow key={form.id}>
                                            <TableCell>
                                                <Typography variant="body2" fontWeight="medium">
                                                    {form.staffName}
                                                </Typography>
                                                <Typography variant="caption" color="text.secondary">
                                                    {form.email}
                                                </Typography>
                                            </TableCell>
                                            <TableCell>
                                                <Chip
                                                    label={form.travelType === 'project' ? 'Project' : 'Travel'}
                                                    size="small"
                                                    color={form.travelType === 'project' ? 'primary' : 'default'}
                                                    variant="outlined"
                                                />
                                            </TableCell>
                                            <TableCell>
                                                {form.travelType === 'project' ? (
                                                    <>
                                                        <Typography variant="body2">{form.projectNumber}</Typography>
                                                        <Typography variant="caption" color="text.secondary">
                                                            {form.projectName}
                                                        </Typography>
                                                    </>
                                                ) : (
                                                    <Typography variant="body2" color="text.secondary">—</Typography>
                                                )}
                                            </TableCell>
                                            <TableCell>
                                                <Typography variant="body2">
                                                    {form.destination.join(', ') || 'Not specified'}
                                                </Typography>
                                            </TableCell>
                                            <TableCell>
                                                <Typography variant="body2">
                                                    {formatDate(form.travelDateFrom)} - {formatDate(form.travelDateTo)}
                                                </Typography>
                                            </TableCell>
                                            <TableCell>
                                                <Chip
                                                    label={form.status.toUpperCase()}
                                                    color={form.status === 'approved' ? 'success' : 'default'}
                                                    size="small"
                                                />
                                            </TableCell>
                                            <TableCell>
                                                <Typography variant="body2">
                                                    {form.submittedDate ? formatDate(form.submittedDate) : 'N/A'}
                                                </Typography>
                                            </TableCell>
                                            <TableCell align="center">
                                                <Button
                                                    variant="outlined"
                                                    size="small"
                                                    onClick={() => handleViewForm(form.id)}
                                                >
                                                    View
                                                </Button>
                                            </TableCell>
                                        </TableRow>
                                    ))}
                                </TableBody>
                            </Table>
                        </TableContainer>
                        {historicalForms.length === 0 && (
                            <Box sx={{ textAlign: 'center', py: 4 }}>
                                <Typography variant="h6" gutterBottom>
                                    No historical travel records
                                </Typography>
                                <Typography variant="body2" color="text.secondary">
                                    Approved and completed travel will appear here
                                </Typography>
                            </Box>
                        )}
                    </TabPanel>

                    {/* Staff Analytics Tab */}
                    <TabPanel value={selectedTab} index={4}>
                        {/* Staff Travel Analytics Implementation */}
                        <Typography variant="h6" gutterBottom>
                            Staff Travel Analytics
                        </Typography>
                        <Grid container spacing={3}>
                            {/* Summary Cards */}
                            <Grid item xs={12} md={3}>
                                <Card>
                                    <CardContent>
                                        <Typography color="text.secondary" gutterBottom>
                                            Total Staff Travelled
                                        </Typography>
                                        <Typography variant="h4">
                                            {new Set(historicalForms.map(f => f.staffName)).size}
                                        </Typography>
                                    </CardContent>
                                </Card>
                            </Grid>
                            <Grid item xs={12} md={3}>
                                <Card>
                                    <CardContent>
                                        <Typography color="text.secondary" gutterBottom>
                                            Total Trips (Approved)
                                        </Typography>
                                        <Typography variant="h4">
                                            {historicalForms.filter(f => f.status === 'approved').length}
                                        </Typography>
                                    </CardContent>
                                </Card>
                            </Grid>
                            <Grid item xs={12} md={3}>
                                <Card>
                                    <CardContent>
                                        <Typography color="text.secondary" gutterBottom>
                                            Unique Destinations
                                        </Typography>
                                        <Typography variant="h4">
                                            {new Set(historicalForms.flatMap(f => f.destination)).size}
                                        </Typography>
                                    </CardContent>
                                </Card>
                            </Grid>
                            <Grid item xs={12} md={3}>
                                <Card>
                                    <CardContent>
                                        <Typography color="text.secondary" gutterBottom>
                                            Avg Trip Duration
                                        </Typography>
                                        <Typography variant="h4">
                                            {historicalForms.length > 0
                                                ? Math.round(
                                                    historicalForms.reduce((sum, f) => {
                                                        const days = Math.ceil(
                                                            (new Date(f.travelDateTo).getTime() -
                                                                new Date(f.travelDateFrom).getTime()) /
                                                            (1000 * 60 * 60 * 24)
                                                        );
                                                        return sum + days;
                                                    }, 0) / historicalForms.length
                                                )
                                                : 0}{' '}
                                            days
                                        </Typography>
                                    </CardContent>
                                </Card>
                            </Grid>

                            {/* Top Travellers */}
                            <Grid item xs={12} md={6}>
                                <Card>
                                    <CardHeader title="Top Travellers" />
                                    <CardContent>
                                        <List>
                                            {Object.entries(
                                                historicalForms
                                                    .filter(f => f.status === 'approved')
                                                    .reduce((acc: any, form) => {
                                                        acc[form.staffName] = (acc[form.staffName] || 0) + 1;
                                                        return acc;
                                                    }, {})
                                            )
                                                .sort(([, a]: any, [, b]: any) => b - a)
                                                .slice(0, 10)
                                                .map(([name, count]: any) => (
                                                    <ListItem key={name}>
                                                        <ListItemText
                                                            primary={name}
                                                            secondary={`${count} trips`}
                                                        />
                                                    </ListItem>
                                                ))}
                                        </List>
                                    </CardContent>
                                </Card>
                            </Grid>

                            {/* Top Destinations */}
                            <Grid item xs={12} md={6}>
                                <Card>
                                    <CardHeader title="Top Destinations" />
                                    <CardContent>
                                        <List>
                                            {Object.entries(
                                                historicalForms
                                                    .filter(f => f.status === 'approved')
                                                    .flatMap(f => f.destination)
                                                    .reduce((acc: any, dest) => {
                                                        acc[dest] = (acc[dest] || 0) + 1;
                                                        return acc;
                                                    }, {})
                                            )
                                                .sort(([, a]: any, [, b]: any) => b - a)
                                                .slice(0, 10)
                                                .map(([dest, count]: any) => (
                                                    <ListItem key={dest}>
                                                        <ListItemText
                                                            primary={dest}
                                                            secondary={`${count} visits`}
                                                        />
                                                    </ListItem>
                                                ))}
                                        </List>
                                    </CardContent>
                                </Card>
                            </Grid>
                        </Grid>
                    </TabPanel>

                    {/* Calendar Tab */}
                    <TabPanel value={selectedTab} index={5}>
                        <TravelCalendar
                            travelForms={travelForms as any}
                            onFormSelect={(form: any) => {
                                setSnackbarMessage(`Selected travel: ${form.staffName} to ${form.destination.join(', ')}`);
                                setSnackbarOpen(true);
                            }}
                        />
                    </TabPanel>

                    {/* Analytics Tab */}
                    <TabPanel value={selectedTab} index={4}>
                        <TravelAnalytics
                            travelForms={travelForms as any}
                            timeRange="month"
                        />
                    </TabPanel>
                </Paper>

                {/* Approval Dialog */}
                <Dialog
                    open={showApprovalDialog}
                    onClose={() => setShowApprovalDialog(false)}
                    maxWidth="sm"
                    fullWidth
                >
                    {selectedForm && (
                        <>
                            <DialogTitle>
                                <Stack direction="row" alignItems="center" spacing={2}>
                                    <CheckCircleIcon color="success" />
                                    Approve Travel Form
                                </Stack>
                            </DialogTitle>
                            <DialogContent>
                                <Typography variant="body2" color="text.secondary" gutterBottom>
                                    Are you sure you want to approve this travel request?
                                </Typography>
                                <Paper sx={{
                                    p: 3,
                                    mt: 2,
                                    backgroundColor: '#1a1a1a',
                                    color: '#ffffff',
                                    border: '1px solid',
                                    borderColor: 'success.main',
                                    borderRadius: 1
                                }}>
                                    <Typography variant="body2" sx={{ mb: 1.5 }}><strong>Staff:</strong> {selectedForm.staffName}</Typography>
                                    <Typography variant="body2" sx={{ mb: 1.5 }}><strong>Destination:</strong> {selectedForm.destination.join(', ')}</Typography>
                                    <Typography variant="body2" sx={{ mb: 1.5 }}><strong>Dates:</strong> {formatDate(selectedForm.travelDateFrom)} - {formatDate(selectedForm.travelDateTo)}</Typography>
                                    <Typography variant="body2"><strong>Project:</strong> {selectedForm.projectName}</Typography>
                                </Paper>
                            </DialogContent>
                            <DialogActions>
                                <Button onClick={() => setShowApprovalDialog(false)}>Cancel</Button>
                                <Button variant="contained" onClick={confirmApproval}>
                                    Approve Travel
                                </Button>
                            </DialogActions>
                        </>
                    )}
                </Dialog>

                {/* View Form Dialog */}
                <Dialog
                    open={showViewDialog}
                    onClose={() => setShowViewDialog(false)}
                    maxWidth="lg"
                    fullWidth
                    PaperProps={{
                        sx: { height: '90vh' }
                    }}
                >
                    {selectedForm && (
                        <>
                            <DialogTitle>
                                <Stack direction="row" alignItems="center" spacing={2}>
                                    <Typography variant="h6">
                                        Travel Form Details - {selectedForm.staffName}
                                    </Typography>
                                    <Chip
                                        label={selectedForm.status.toUpperCase()}
                                        color={selectedForm.status === 'submitted' ? 'info' : 'warning'}
                                        size="small"
                                    />
                                </Stack>
                            </DialogTitle>
                            <DialogContent dividers sx={{ p: 3, overflowY: 'auto' }}>
                                {loadingFormDetails ? (
                                    <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 200 }}>
                                        <Typography>Loading form details...</Typography>
                                    </Box>
                                ) : (
                                    <Grid container spacing={2}>
                                        {/* Left Column */}
                                        <Grid item xs={6}>
                                            <Stack spacing={2}>
                                                {/* Traveller Info */}
                                                <Box>
                                                    <Typography variant="subtitle1" fontWeight="bold" gutterBottom sx={{ borderBottom: 1, borderColor: 'divider', pb: 0.5 }}>
                                                        Traveller Information
                                                    </Typography>
                                                    <Stack spacing={1} sx={{ mt: 1 }}>
                                                        <Box>
                                                            <Typography variant="caption" color="text.secondary">Name</Typography>
                                                            <Typography variant="body2" fontWeight="medium">{selectedForm.staffName}</Typography>
                                                        </Box>
                                                        <Box>
                                                            <Typography variant="caption" color="text.secondary">Email</Typography>
                                                            <Typography variant="body2">{selectedForm.email}</Typography>
                                                        </Box>
                                                    </Stack>
                                                </Box>

                                                {/* Project Details */}
                                                <Box>
                                                    <Typography variant="subtitle1" fontWeight="bold" gutterBottom sx={{ borderBottom: 1, borderColor: 'divider', pb: 0.5 }}>
                                                        Project Details
                                                    </Typography>
                                                    <Stack spacing={1} sx={{ mt: 1 }}>
                                                        <Box>
                                                            <Typography variant="caption" color="text.secondary">Project Number</Typography>
                                                            <Typography variant="body2" fontWeight="medium">{selectedForm.projectNumber}</Typography>
                                                        </Box>
                                                        <Box>
                                                            <Typography variant="caption" color="text.secondary">Project Name</Typography>
                                                            <Typography variant="body2">{selectedForm.projectName}</Typography>
                                                        </Box>
                                                        <Box>
                                                            <Typography variant="caption" color="text.secondary">Client</Typography>
                                                            <Typography variant="body2">{selectedForm.client}</Typography>
                                                        </Box>
                                                        {fullFormData?.team_leader && (
                                                            <Box>
                                                                <Typography variant="caption" color="text.secondary">Team Leader</Typography>
                                                                <Typography variant="body2">{fullFormData.team_leader}</Typography>
                                                            </Box>
                                                        )}
                                                        {fullFormData?.duty_officer && (
                                                            <Box>
                                                                <Typography variant="caption" color="text.secondary">Duty Officer</Typography>
                                                                <Typography variant="body2">{fullFormData.duty_officer}</Typography>
                                                            </Box>
                                                        )}
                                                    </Stack>
                                                </Box>

                                                {/* Travel Dates */}
                                                <Box>
                                                    <Typography variant="subtitle1" fontWeight="bold" gutterBottom sx={{ borderBottom: 1, borderColor: 'divider', pb: 0.5 }}>
                                                        Travel Period
                                                    </Typography>
                                                    <Stack spacing={1} sx={{ mt: 1 }}>
                                                        <Box>
                                                            <Typography variant="caption" color="text.secondary">From</Typography>
                                                            <Typography variant="body2" fontWeight="medium">{formatDate(selectedForm.travelDateFrom)}</Typography>
                                                        </Box>
                                                        <Box>
                                                            <Typography variant="caption" color="text.secondary">To</Typography>
                                                            <Typography variant="body2" fontWeight="medium">{formatDate(selectedForm.travelDateTo)}</Typography>
                                                        </Box>
                                                        <Box>
                                                            <Typography variant="caption" color="text.secondary">Duration</Typography>
                                                            <Typography variant="body2">{getDaysUntilTravel(selectedForm.travelDateFrom)} days</Typography>
                                                        </Box>
                                                    </Stack>
                                                </Box>
                                            </Stack>
                                        </Grid>

                                        {/* Right Column */}
                                        <Grid item xs={6}>
                                            <Stack spacing={2}>
                                                {/* Destination */}
                                                <Box>
                                                    <Typography variant="subtitle1" fontWeight="bold" gutterBottom sx={{ borderBottom: 1, borderColor: 'divider', pb: 0.5 }}>
                                                        Destination
                                                    </Typography>
                                                    <Typography variant="body2" sx={{ mt: 1 }}>{selectedForm.destination.join(', ') || 'Not specified'}</Typography>
                                                </Box>

                                                {/* Form Summary */}
                                                <Box>
                                                    <Typography variant="subtitle1" fontWeight="bold" gutterBottom sx={{ borderBottom: 1, borderColor: 'divider', pb: 0.5 }}>
                                                        Form Summary
                                                    </Typography>
                                                    <Grid container spacing={1} sx={{ mt: 1 }}>
                                                        <Grid item xs={6}>
                                                            <Typography variant="caption" color="text.secondary">Accommodations</Typography>
                                                            <Typography variant="body2">{fullFormData?.accommodations?.length ?? '—'}</Typography>
                                                        </Grid>
                                                        <Grid item xs={6}>
                                                            <Typography variant="caption" color="text.secondary">Travel Segments</Typography>
                                                            <Typography variant="body2">{fullFormData?.segments?.length ?? '—'}</Typography>
                                                        </Grid>
                                                    </Grid>
                                                </Box>

                                                {/* Submission Info */}
                                                <Box>
                                                    <Typography variant="subtitle1" fontWeight="bold" gutterBottom sx={{ borderBottom: 1, borderColor: 'divider', pb: 0.5 }}>
                                                        Submission Details
                                                    </Typography>
                                                    <Stack spacing={1} sx={{ mt: 1 }}>
                                                        <Box>
                                                            <Typography variant="caption" color="text.secondary">Status</Typography>
                                                            <Typography variant="body2">
                                                                <Chip
                                                                    label={selectedForm.status.toUpperCase()}
                                                                    color={selectedForm.status === 'submitted' ? 'info' : 'warning'}
                                                                    size="small"
                                                                />
                                                            </Typography>
                                                        </Box>
                                                        {selectedForm.submittedDate && (
                                                            <Box>
                                                                <Typography variant="caption" color="text.secondary">Submitted</Typography>
                                                                <Typography variant="body2">{formatDate(selectedForm.submittedDate)}</Typography>
                                                            </Box>
                                                        )}
                                                        <Box>
                                                            <Typography variant="caption" color="text.secondary">Last Modified</Typography>
                                                            <Typography variant="body2">{formatDate(selectedForm.lastModified)}</Typography>
                                                        </Box>
                                                    </Stack>
                                                </Box>
                                            </Stack>
                                        </Grid>

                                        {/* Full Width Sections */}
                                        {fullFormData && (
                                            <>
                                                {/* Accommodations */}
                                                {fullFormData.accommodations && fullFormData.accommodations.length > 0 && (
                                                    <Grid item xs={12}>
                                                        <Typography variant="subtitle1" fontWeight="bold" gutterBottom sx={{ borderBottom: 1, borderColor: 'divider', pb: 0.5 }}>
                                                            Accommodations ({fullFormData.accommodations.length})
                                                        </Typography>
                                                        <Stack spacing={1.5} sx={{ mt: 1 }}>
                                                            {fullFormData.accommodations.map((accom: any, idx: number) => (
                                                                <Paper key={idx} sx={{ p: 1.5, bgcolor: 'background.default' }}>
                                                                    <Grid container spacing={1}>
                                                                        <Grid item xs={3}>
                                                                            <Typography variant="caption" color="text.secondary">Check-in</Typography>
                                                                            <Typography variant="body2">{new Date(accom.check_in_date).toLocaleDateString()}</Typography>
                                                                        </Grid>
                                                                        <Grid item xs={3}>
                                                                            <Typography variant="caption" color="text.secondary">Check-out</Typography>
                                                                            <Typography variant="body2">{new Date(accom.check_out_date).toLocaleDateString()}</Typography>
                                                                        </Grid>
                                                                        <Grid item xs={2}>
                                                                            <Typography variant="caption" color="text.secondary">Nights</Typography>
                                                                            <Typography variant="body2">{accom.nights || 'N/A'}</Typography>
                                                                        </Grid>
                                                                        <Grid item xs={4}>
                                                                            <Typography variant="caption" color="text.secondary">Location</Typography>
                                                                            <Typography variant="body2">{accom.location || 'Not specified'}</Typography>
                                                                        </Grid>
                                                                        <Grid item xs={6}>
                                                                            <Typography variant="caption" color="text.secondary">Hotel</Typography>
                                                                            <Typography variant="body2">{accom.hotel_name || 'Not specified'}</Typography>
                                                                        </Grid>
                                                                        <Grid item xs={6}>
                                                                            <Typography variant="caption" color="text.secondary">Phone</Typography>
                                                                            <Typography variant="body2">{accom.phone || 'Not specified'}</Typography>
                                                                        </Grid>
                                                                        {accom.address && (
                                                                            <Grid item xs={12}>
                                                                                <Typography variant="caption" color="text.secondary">Address</Typography>
                                                                                <Typography variant="body2">{accom.address}</Typography>
                                                                            </Grid>
                                                                        )}
                                                                    </Grid>
                                                                </Paper>
                                                            ))}
                                                        </Stack>
                                                    </Grid>
                                                )}

                                                {/* Travel Segments */}
                                                {fullFormData.segments && fullFormData.segments.length > 0 && (
                                                    <Grid item xs={12}>
                                                        <Typography variant="subtitle1" fontWeight="bold" gutterBottom sx={{ borderBottom: 1, borderColor: 'divider', pb: 0.5 }}>
                                                            Travel Segments ({fullFormData.segments.length})
                                                        </Typography>
                                                        <Stack spacing={1.5} sx={{ mt: 1 }}>
                                                            {fullFormData.segments.map((seg: any, idx: number) => (
                                                                <Paper key={idx} sx={{ p: 1.5, bgcolor: 'background.default' }}>
                                                                    <Grid container spacing={1}>
                                                                        <Grid item xs={3}>
                                                                            <Typography variant="caption" color="text.secondary">Departure</Typography>
                                                                            <Typography variant="body2">{seg.travel_date && seg.departure_time ? `${seg.travel_date} ${seg.departure_time}` : seg.travel_date || 'Not specified'}</Typography>
                                                                        </Grid>
                                                                        <Grid item xs={3}>
                                                                            <Typography variant="caption" color="text.secondary">Arrival</Typography>
                                                                            <Typography variant="body2">{seg.travel_date && seg.arrival_time ? `${seg.travel_date} ${seg.arrival_time}` : seg.arrival_time || 'Not specified'}</Typography>
                                                                        </Grid>
                                                                        <Grid item xs={3}>
                                                                            <Typography variant="caption" color="text.secondary">From</Typography>
                                                                            <Typography variant="body2">{seg.route_from || 'Not specified'}</Typography>
                                                                        </Grid>
                                                                        <Grid item xs={3}>
                                                                            <Typography variant="caption" color="text.secondary">To</Typography>
                                                                            <Typography variant="body2">{seg.route_to || 'Not specified'}</Typography>
                                                                        </Grid>
                                                                        <Grid item xs={3}>
                                                                            <Typography variant="caption" color="text.secondary">Type</Typography>
                                                                            <Typography variant="body2">{seg.travel_type || 'Not specified'}</Typography>
                                                                        </Grid>
                                                                        {seg.flight_number && (
                                                                            <Grid item xs={3}>
                                                                                <Typography variant="caption" color="text.secondary">Flight #</Typography>
                                                                                <Typography variant="body2">{seg.flight_number}</Typography>
                                                                            </Grid>
                                                                        )}
                                                                        {seg.booking_ref && (
                                                                            <Grid item xs={3}>
                                                                                <Typography variant="caption" color="text.secondary">Booking Ref</Typography>
                                                                                <Typography variant="body2">{seg.booking_ref}</Typography>
                                                                            </Grid>
                                                                        )}
                                                                    </Grid>
                                                                </Paper>
                                                            ))}
                                                        </Stack>
                                                    </Grid>
                                                )}

                                                {/* Client Contact Details & Countries */}
                                                {fullFormData && (
                                                    <Grid item xs={12}>
                                                        <Typography variant="subtitle1" fontWeight="bold" gutterBottom sx={{ borderBottom: 1, borderColor: 'divider', pb: 0.5 }}>
                                                            Client Contact & Travel Details
                                                        </Typography>
                                                        <Grid container spacing={2} sx={{ mt: 0.5 }}>
                                                            {fullFormData.client_contact_name && (
                                                                <Grid item xs={6} md={4}>
                                                                    <Typography variant="caption" color="text.secondary">Client Contact Name</Typography>
                                                                    <Typography variant="body2">{fullFormData.client_contact_name}</Typography>
                                                                </Grid>
                                                            )}
                                                            {fullFormData.client_contact_email && (
                                                                <Grid item xs={6} md={4}>
                                                                    <Typography variant="caption" color="text.secondary">Client Email</Typography>
                                                                    <Typography variant="body2">{fullFormData.client_contact_email}</Typography>
                                                                </Grid>
                                                            )}
                                                            {fullFormData.client_contact_phone && (
                                                                <Grid item xs={6} md={4}>
                                                                    <Typography variant="caption" color="text.secondary">Client Phone</Typography>
                                                                    <Typography variant="body2">{fullFormData.client_contact_phone}</Typography>
                                                                </Grid>
                                                            )}
                                                            {fullFormData.fcdo_restrictions && (
                                                                <Grid item xs={12}>
                                                                    <Typography variant="caption" color="text.secondary">FCDO Restrictions</Typography>
                                                                    <Typography variant="body2">{fullFormData.fcdo_restrictions}</Typography>
                                                                </Grid>
                                                            )}
                                                        </Grid>
                                                    </Grid>
                                                )}

                                                {/* Pre-Deployment Checks */}
                                                {fullFormData && (
                                                    <Grid item xs={12}>
                                                        <Typography variant="subtitle1" fontWeight="bold" gutterBottom sx={{ borderBottom: 1, borderColor: 'divider', pb: 0.5 }}>
                                                            Pre-Deployment Checks
                                                        </Typography>
                                                        <Grid container spacing={2} sx={{ mt: 0.5 }}>
                                                            {fullFormData.bamboo_updated && (
                                                                <Grid item xs={6} md={4}>
                                                                    <Typography variant="caption" color="text.secondary">Bamboo Updated</Typography>
                                                                    <Chip label={fullFormData.bamboo_updated === 'Y' ? 'Yes' : 'No'} size="small" color={fullFormData.bamboo_updated === 'Y' ? 'success' : 'default'} />
                                                                </Grid>
                                                            )}
                                                            {fullFormData.pre_deployment_brief && (
                                                                <Grid item xs={6} md={4}>
                                                                    <Typography variant="caption" color="text.secondary">Pre-Deployment Brief</Typography>
                                                                    <Chip label={fullFormData.pre_deployment_brief === 'Y' ? 'Yes' : 'No'} size="small" color={fullFormData.pre_deployment_brief === 'Y' ? 'success' : 'default'} />
                                                                </Grid>
                                                            )}
                                                            {fullFormData.happy_to_travel_location && (
                                                                <Grid item xs={6} md={4}>
                                                                    <Typography variant="caption" color="text.secondary">Happy to Travel (Location)</Typography>
                                                                    <Chip label={fullFormData.happy_to_travel_location === 'Y' ? 'Yes' : 'No'} size="small" color={fullFormData.happy_to_travel_location === 'Y' ? 'success' : 'default'} />
                                                                </Grid>
                                                            )}
                                                            {fullFormData.happy_to_travel_companions && (
                                                                <Grid item xs={6} md={4}>
                                                                    <Typography variant="caption" color="text.secondary">Happy to Travel (Companions)</Typography>
                                                                    <Chip label={fullFormData.happy_to_travel_companions === 'Y' ? 'Yes' : 'No'} size="small" color={fullFormData.happy_to_travel_companions === 'Y' ? 'success' : 'default'} />
                                                                </Grid>
                                                            )}
                                                            {fullFormData.safe_heat_training && (
                                                                <Grid item xs={6} md={4}>
                                                                    <Typography variant="caption" color="text.secondary">SAFE & HEAT Training</Typography>
                                                                    <Chip label={fullFormData.safe_heat_training === 'Y' ? 'Yes' : 'No'} size="small" color={fullFormData.safe_heat_training === 'Y' ? 'success' : 'default'} />
                                                                </Grid>
                                                            )}
                                                            {fullFormData.security_clearance && (
                                                                <Grid item xs={6} md={4}>
                                                                    <Typography variant="caption" color="text.secondary">Security Clearance</Typography>
                                                                    <Typography variant="body2">{fullFormData.security_clearance}</Typography>
                                                                </Grid>
                                                            )}
                                                            {fullFormData.medical_clearance && (
                                                                <Grid item xs={6} md={4}>
                                                                    <Typography variant="caption" color="text.secondary">Medical Clearance</Typography>
                                                                    <Chip label={fullFormData.medical_clearance === 'Y' ? 'Yes' : 'No'} size="small" color={fullFormData.medical_clearance === 'Y' ? 'success' : 'default'} />
                                                                </Grid>
                                                            )}
                                                            {fullFormData.visa_obtained && (
                                                                <Grid item xs={6} md={4}>
                                                                    <Typography variant="caption" color="text.secondary">Visa Obtained</Typography>
                                                                    <Chip label={fullFormData.visa_obtained === 'Y' ? 'Yes' : 'No'} size="small" color={fullFormData.visa_obtained === 'Y' ? 'success' : 'default'} />
                                                                </Grid>
                                                            )}
                                                            {fullFormData.doctor_advised_not_travel && (
                                                                <Grid item xs={6} md={4}>
                                                                    <Typography variant="caption" color="text.secondary">Doctor Advised Not to Travel</Typography>
                                                                    <Chip label={fullFormData.doctor_advised_not_travel === 'Y' ? 'Yes' : 'No'} size="small" color={fullFormData.doctor_advised_not_travel === 'Y' ? 'warning' : 'success'} />
                                                                </Grid>
                                                            )}
                                                            {fullFormData.pre_existing_condition_understood && (
                                                                <Grid item xs={6} md={4}>
                                                                    <Typography variant="caption" color="text.secondary">Pre-Existing Conditions Understood</Typography>
                                                                    <Chip label={fullFormData.pre_existing_condition_understood === 'Y' ? 'Yes' : 'No'} size="small" color={fullFormData.pre_existing_condition_understood === 'Y' ? 'success' : 'default'} />
                                                                </Grid>
                                                            )}
                                                        </Grid>
                                                    </Grid>
                                                )}

                                                {/* Contact & Communications */}
                                                {fullFormData && (
                                                    <Grid item xs={12}>
                                                        <Typography variant="subtitle1" fontWeight="bold" gutterBottom sx={{ borderBottom: 1, borderColor: 'divider', pb: 0.5 }}>
                                                            Contact & Communications
                                                        </Typography>
                                                        <Grid container spacing={2} sx={{ mt: 0.5 }}>
                                                            {fullFormData.whatsapp_phone && (
                                                                <Grid item xs={6} md={4}>
                                                                    <Typography variant="caption" color="text.secondary">WhatsApp Phone</Typography>
                                                                    <Typography variant="body2">{fullFormData.whatsapp_phone}</Typography>
                                                                </Grid>
                                                            )}
                                                            {fullFormData.focal_in_country_name && (
                                                                <Grid item xs={6} md={4}>
                                                                    <Typography variant="caption" color="text.secondary">Focal Point in Country</Typography>
                                                                    <Typography variant="body2">{fullFormData.focal_in_country_name}</Typography>
                                                                </Grid>
                                                            )}
                                                            {fullFormData.focal_in_country_contact && (
                                                                <Grid item xs={6} md={4}>
                                                                    <Typography variant="caption" color="text.secondary">Focal Point Contact</Typography>
                                                                    <Typography variant="body2">{fullFormData.focal_in_country_contact}</Typography>
                                                                </Grid>
                                                            )}
                                                            {fullFormData.client_contact_name_alt && (
                                                                <Grid item xs={6} md={4}>
                                                                    <Typography variant="caption" color="text.secondary">Alt Client Contact Name</Typography>
                                                                    <Typography variant="body2">{fullFormData.client_contact_name_alt}</Typography>
                                                                </Grid>
                                                            )}
                                                            {fullFormData.client_contact_details_alt && (
                                                                <Grid item xs={6} md={4}>
                                                                    <Typography variant="caption" color="text.secondary">Alt Client Contact Details</Typography>
                                                                    <Typography variant="body2">{fullFormData.client_contact_details_alt}</Typography>
                                                                </Grid>
                                                            )}
                                                            {fullFormData.travelling_with_iod && (
                                                                <Grid item xs={6} md={4}>
                                                                    <Typography variant="caption" color="text.secondary">Travelling with IOD PARC</Typography>
                                                                    <Chip label={fullFormData.travelling_with_iod === 'Y' ? 'Yes' : 'No'} size="small" color={fullFormData.travelling_with_iod === 'Y' ? 'info' : 'default'} />
                                                                </Grid>
                                                            )}
                                                            {fullFormData.travelling_with_name && (
                                                                <Grid item xs={12}>
                                                                    <Typography variant="caption" color="text.secondary">Travelling With (Names)</Typography>
                                                                    <Typography variant="body2">{fullFormData.travelling_with_name}</Typography>
                                                                </Grid>
                                                            )}
                                                        </Grid>
                                                    </Grid>
                                                )}

                                                {/* Travel & Logistics */}
                                                {fullFormData && (
                                                    <Grid item xs={12}>
                                                        <Typography variant="subtitle1" fontWeight="bold" gutterBottom sx={{ borderBottom: 1, borderColor: 'divider', pb: 0.5 }}>
                                                            Travel & Logistics
                                                        </Typography>
                                                        <Grid container spacing={2} sx={{ mt: 0.5 }}>
                                                            {fullFormData.airport_transfers_arranged && (
                                                                <Grid item xs={6} md={4}>
                                                                    <Typography variant="caption" color="text.secondary">Airport Transfers Arranged</Typography>
                                                                    <Chip label={fullFormData.airport_transfers_arranged === 'Y' ? 'Yes' : 'No'} size="small" color={fullFormData.airport_transfers_arranged === 'Y' ? 'success' : 'default'} />
                                                                </Grid>
                                                            )}
                                                            {fullFormData.in_country_transport && (
                                                                <Grid item xs={6} md={4}>
                                                                    <Typography variant="caption" color="text.secondary">In-Country Transport</Typography>
                                                                    <Typography variant="body2">{fullFormData.in_country_transport}</Typography>
                                                                </Grid>
                                                            )}
                                                            {fullFormData.itinerary_forwarded && (
                                                                <Grid item xs={6} md={4}>
                                                                    <Typography variant="caption" color="text.secondary">Itinerary Forwarded</Typography>
                                                                    <Chip label={fullFormData.itinerary_forwarded === 'Y' ? 'Yes' : 'No'} size="small" color={fullFormData.itinerary_forwarded === 'Y' ? 'success' : 'default'} />
                                                                </Grid>
                                                            )}
                                                        </Grid>
                                                    </Grid>
                                                )}
                                            </>
                                        )}
                                    </Grid>
                                )}
                            </DialogContent>
                            <DialogActions>
                                <Button onClick={() => setShowViewDialog(false)}>Close</Button>
                                {selectedForm.status === 'submitted' && (
                                    <Button
                                        variant="contained"
                                        color="success"
                                        onClick={() => {
                                            setShowViewDialog(false);
                                            handleApproveForm(selectedForm.id);
                                        }}
                                    >
                                        Approve Travel
                                    </Button>
                                )}
                            </DialogActions>
                        </>
                    )}
                </Dialog>

                {/* Snackbar */}
                <Snackbar
                    open={snackbarOpen}
                    autoHideDuration={4000}
                    onClose={() => setSnackbarOpen(false)}
                >
                    <Alert onClose={() => setSnackbarOpen(false)} severity="success">
                        {snackbarMessage}
                    </Alert>
                </Snackbar>
            </Box>
        </Container>
    );
};

export default TravelDashboardPage;
